import { Button, TextBox } from '@idexx-laboratories/spot-react';
import dayjs from 'dayjs';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import CalendarWithOffset from '../../components/CalendarWithOffset/CalendarWithOffset';
import FieldGroupInCell from '../../components/FieldGroupInCell/FieldGroupInCell';
import { GridRenderer, GridRendererProps } from '../../components/Grid/Grid';
import { useStore } from '../../hooks/use-store';
import { useCheckSinglePermission } from '../../security/security-hook';
import { Event } from '../../types/event-types';
import {
  PERMISSION_ALLOW,
  RESOURCE_SERVICE_EVENTS_DELETE,
  RESOURCE_SERVICE_EVENTS_UPDATE,
} from '../../types/users-types';
import { localUtcOffset } from '../../utils/date-utils';
import { toLocalDateTimeWithTimezoneName } from './ServiceEventsStore';

interface RowCellRendererProps extends GridRendererProps<Event<number>> {
  field: keyof Event<number>;
}

const RowCellRenderer = observer((props: RowCellRendererProps) => {
  const { row, index, field } = props;
  const { serviceEventsStore: store } = useStore();
  const { eventIntervalsHandler } = store;

  const reasonRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    reasonRef.current?.focus();
  }, []);

  if (store.currentItemIndex === index) {
    if (field === 'startDateTime') {
      return (
        <>
          <FieldGroupInCell
            htmlFor={`table-cell-text-box_${field}`}
            errorText={store.currentItemError[field]}
          >
            <TextBox
              id={`table-cell-text-box_${field}`}
              data-testid={`table-cell-text-box_${field}`}
              readOnly
              value={toLocalDateTimeWithTimezoneName(store.currentItem.startDateTime)}
              disabled={store.loaders.action}
            />
          </FieldGroupInCell>
          <div className='date-wrapper'>
            <CalendarWithOffset
              type='Date'
              value={store.currentItem.startDateTime}
              onChange={timestamp =>
                runInAction(() => (store.currentItem.startDateTime = timestamp))
              }
              eventIntervalsHandler={eventIntervalsHandler}
              utcOffset={localUtcOffset(new Date())}
            />
          </div>
          <div className='time-wrapper'>
            <CalendarWithOffset
              type='Time'
              value={store.currentItem.startDateTime}
              onChange={timestamp =>
                runInAction(() => (store.currentItem.startDateTime = timestamp))
              }
              eventIntervalsHandler={eventIntervalsHandler}
              utcOffset={localUtcOffset(new Date())}
            />
          </div>
        </>
      );
    }
    if (field === 'endDateTime') {
      return (
        <>
          <FieldGroupInCell
            htmlFor={`table-cell-text-box_${field}`}
            errorText={store.currentItemError[field]}
          >
            <TextBox
              id={`table-cell-text-box_${field}`}
              data-testid={`table-cell-text-box_${field}`}
              readOnly
              value={toLocalDateTimeWithTimezoneName(store.currentItem.endDateTime)}
              disabled={store.loaders.action}
            />
          </FieldGroupInCell>
          <div className='date-wrapper'>
            <CalendarWithOffset
              type='Date'
              value={store.currentItem.endDateTime}
              onChange={timestamp => runInAction(() => (store.currentItem.endDateTime = timestamp))}
              eventIntervalsHandler={eventIntervalsHandler}
              utcOffset={localUtcOffset(new Date())}
            />
          </div>
          <div className='time-wrapper'>
            <CalendarWithOffset
              type='Time'
              value={store.currentItem.endDateTime}
              onChange={timestamp => runInAction(() => (store.currentItem.endDateTime = timestamp))}
              eventIntervalsHandler={eventIntervalsHandler}
              utcOffset={localUtcOffset(new Date())}
            />
          </div>
        </>
      );
    }
    if (field === 'reason') {
      return (
        <FieldGroupInCell
          htmlFor={`table-cell-text-box_${field}`}
          errorText={index === store.currentItemIndex ? store.currentItemError[field] : undefined}
        >
          <TextBox
            ref={reasonRef}
            id={`table-cell-text-box_${field}`}
            data-testid={`table-cell-text-box_${field}`}
            value={store.currentItem[field] || ''}
            disabled={store.loaders.action}
            maxLength={100}
            onChange={e => runInAction(() => (store.currentItem[field] = e.target.value))}
          />
        </FieldGroupInCell>
      );
    }
  }
  return (
    <div
      className='plain-text item-editors'
      role='table-cell-text'
      aria-label={`Item ${String(field)}`}
    >
      {field === 'startDateTime' || field === 'endDateTime'
        ? toLocalDateTimeWithTimezoneName(row[field])
        : row[field]}
    </div>
  );
});

export const ActionsRenderer = observer((props: GridRendererProps<Event<number>>) => {
  const { row, index } = props;
  const { serviceEventsStore: store } = useStore();

  const editMode = store.currentItemIndex !== undefined;
  const disabled = store.loaders.action;
  const deleteItemIsAllowed = useCheckSinglePermission(
    RESOURCE_SERVICE_EVENTS_DELETE,
    PERMISSION_ALLOW,
  );
  const updateItemIsAllowed = useCheckSinglePermission(
    RESOURCE_SERVICE_EVENTS_UPDATE,
    PERMISSION_ALLOW,
  );
  if (store.currentItemIndex === index)
    return (
      <>
        <Button
          variant='link'
          iconOnly='save'
          size='small'
          disabled={disabled}
          onClick={() => void store.validateAndUpdateItem()}
          title='Save changes'
          aria-label='Save Changes'
          data-testid={`save-${row.id}-btn`}
        />
        <Button
          variant='link'
          iconOnly='cancel'
          size='small'
          disabled={disabled}
          onClick={() => store.cancelEditingItem()}
          title='Cancel changes'
          aria-label='Cancel Changes'
          data-testid={`cancel-${row.id}-btn`}
        />
      </>
    );
  return (
    <>
      <Button
        variant='link'
        iconOnly='edit'
        size='small'
        disabled={
          disabled || !updateItemIsAllowed || editMode || dayjs().valueOf() > row.startDateTime
        }
        onClick={() => store.editItem(index)}
        title='Edit event'
        aria-label='Edit Event'
        data-testid={`edit-${row.id}-btn`}
      />
      <Button
        variant='link'
        iconOnly='delete'
        size='small'
        disabled={disabled || !deleteItemIsAllowed || editMode}
        onClick={() => store.deleteItem(index)}
        title='Delete event'
        aria-label='Delete Event'
        data-testid={`delete-${row.id}-btn`}
      />
    </>
  );
});

export const ConflictsExistMessage = (userEmail: string) => (): React.JSX.Element => (
  <>
    <p>
      The event was saved, but there were upgrades scheduled between the chosen start and end times.
    </p>
    <p>
      A report of impacted practices was emailed to <strong>{userEmail}</strong> and
      mycornerstonesupport@idexx.com
    </p>
  </>
);

export const rowCellRenderer =
  (field: keyof Event<number>): GridRenderer<Event<number>> =>
  (props: GridRendererProps<Event<number>>) => (
    <RowCellRenderer row={props.row} index={props.index} field={field} />
  );
